import { Injectable } from "@angular/core";
import { Login } from "../models/Login";
import {
    LoginClient,
    LoginResponse,
    LoginRequest,
    UserRight,
    LinkLoginResponse,
    PasswordResetRequest,
    PasswordResetResponse,
    CustomerClient,
    GetCustomerResponse,
} from "../WebApiClient";
type Customer = GetCustomerResponse;
import { BehaviorSubject, Observable } from "rxjs";
import { LinkLogin } from "../models/LinkLogin";

@Injectable()
export class LoginService {

    private static readonly userTokenKey = "userToken";

    private readonly currentUserSubject = new BehaviorSubject<
        Login | LinkLogin | undefined
        >(undefined);
    public currentUser$ = this.currentUserSubject.asObservable();

    private currentCustomerSubject = new BehaviorSubject<Customer | null>(null);
    public currentCustomer$ = this.currentCustomerSubject.asObservable();

    constructor(private readonly webLogin: LoginClient,
        private readonly customerClient: CustomerClient) {
        let tokenJson = sessionStorage.getItem(LoginService.userTokenKey);
        if (tokenJson) {
            try {
                this._loginResponse = JSON.parse(tokenJson);
                if (this._loginResponse && this.isLoginResponse(this._loginResponse)) {
                    this.customerId = this._loginResponse.customerId;
                } else {
                    this.customerId = undefined;
                }
                this.loadCurrentCustomer();
            } catch (e) {
                this._loginResponse = undefined;
            }
        } else {
            this._loginResponse = undefined;
        }
        if (this._loginResponse) {
            this.currentUserSubject.next(this.currentUser);
        }
    }

    private _loginResponse:
        | LoginResponse
        | LinkLoginResponse
        | undefined;
    private get loginResponse():
        | LoginResponse
        | LinkLoginResponse
        | undefined {
        return this._loginResponse;
    }
    private set loginResponse(
        val: LoginResponse | LinkLoginResponse | undefined
    ) {
        this._loginResponse = val;
        sessionStorage.setItem(LoginService.userTokenKey, JSON.stringify(val));
    }

    public async init(): Promise<void> {
        if (this.customerId) {
            await this.loadCurrentCustomer();
        }
    }

    public get loginToken(): string | undefined {
        return this.loginResponse?.token;
    }

    public get currentUserObservable(): Observable<
        Login | LinkLogin | undefined
    > {
        return this.currentUserSubject;
    }

    public async login(username: string, password: string): Promise<boolean> {
        this.loginResponse = undefined;
        try {
            let token = await this.webLogin
                .login(
                    new LoginRequest({
                        username: username,
                        password: password,
                    })
                )
                .toPromise();

            this.loginResponse = token;
            this.mustResetPassword = token.passwordReset;
            this.customerId = token.customerId;

            this.currentUserSubject.next(this.currentUser);

            await this.loadCurrentCustomer();

            return !!token;
        } catch (e) {
            this.currentUserSubject.next(this.currentUser);
            return false;
        }
    }

    public logout() {
        sessionStorage.removeItem(LoginService.userTokenKey);
    }

    public async LoginWithLink(linkId: string): Promise<string | undefined> {
        this.loginResponse = undefined;
        try {
            const token = await this.webLogin.loginWithLink(linkId).toPromise();
            this.loginResponse = token;
            this.currentUserSubject.next(this.currentUser);
            return token?.projectId;
        } catch (e) {
            this.currentUserSubject.next(this.currentUser);
            return;
        }
    }

    public async createProjectLink(projectId: string): Promise<string> {
        var tok = await this.webLogin.createProjectLink(projectId).toPromise();
        return tok;
    }

    public get currentUser(): Login | LinkLogin | undefined {
        const lr = this.loginResponse;
        if (!lr) return;
        if (this.isLoginResponse(lr)) {
            let result = new Login(lr.userId, lr.username, lr.userRights, lr.resourceId!);
            return result;
        }
        const result = new LinkLogin(lr.projectId, lr.userRights,'');
        return result;
    }

    public async passwordReset(username: string) : Promise<PasswordResetResponse> {
        var result = await this.webLogin.passwordReset(
            new PasswordResetRequest({
                username: username,
            })
        ).toPromise();

        return result;
    }

    /**
     * returns true if user has all the userRights asked for.
     */
    hasRights(...userRights: UserRight[]) {
        return userRights.every(
            (r) => (this.currentUser?.userRights.indexOf(r) ?? -1) >= 0
        );
    }

    private isLoginResponse(
        val: LoginResponse | LinkLoginResponse
    ): val is LoginResponse {
        return !!(val as LoginResponse).userId;
    }

    public async loadCurrentCustomer(): Promise<void> {

        if (!this.customerId) {
            console.log("No customerId, setting currentCustomer to null");
            this.currentCustomerSubject.next(null);
            return;
        }

        try {
            const customer = await this.customerClient.getCurrentCustomer().toPromise();
            this.currentCustomerSubject.next(customer);
        } catch (error) {
            console.warn("Failed to load current customer", error);
            this.currentCustomerSubject.next(null);
        }
    }

    public get currentCustomer(): Customer | null {
        return this.currentCustomerSubject.value;
    }

    public lastFailedNavigation?: string = undefined;
    public mustResetPassword: boolean = false;
    public customerId?: string = undefined;
}
